/**
 * The docs' page on measurement and verification: what counts as reach on each platform and when
 * it is read, the checks every item goes through in their fixed order, and the rules that keep
 * the models' judgment open to checking. The statements follow the V1 specification
 * (docs/specification.md, sections 7 and 8).
 */

import { GuideSteps } from "../guides/parts.js";
import { DocPage, DocSection, DocTable, P, Points } from "./parts.js";

/** The checks in the order they run, from objective rules to judgment. */
const checks = [
  {
    title: "Rules",
    text: "The post is public, published inside the campaign window by an account linked to the creator's wallet, within the campaign's limits, and it carries the required link, hashtag, mention and disclosure.",
  },
  {
    title: "Video checks on YouTube",
    text: "The creator uploads the source video. It must match the published one, and its transcript must show the brand as the format requires.",
  },
  {
    title: "Manipulation audit",
    text: "Before any model reads the content, a separate check looks for text that tries to steer the evaluation.",
  },
  {
    title: "Content checks",
    text: "A language model evaluates the content against each requirement of the brief. Each verdict needs a word-for-word quote as evidence.",
  },
  {
    title: "Visual checks",
    text: "A vision model looks for each visual requirement, with the brand's own images as references.",
  },
  {
    title: "Fraud",
    text: "Reach at least ten times the account's own baseline goes to a person. Suspicion alone never excludes an item.",
  },
];

export function Verification() {
  return (
    <DocPage
      group="Protocol"
      title="Verification"
      lead="Morcast counts only organic reach, read from the platform itself, and only for content that passes every check."
    >
      <DocSection id="measurement" title="Measurement">
        <DocTable
          caption="What counts as reach on each platform and when it is read"
          head={["Platform", "What counts", "When it is read"]}
          rows={[
            [
              "X",
              "Organic impressions of the post, read with the author's authorization. Promoted impressions are excluded",
              "At the campaign's end",
            ],
            [
              "YouTube, dedicated video",
              "Organic views during the campaign. Views from advertising are excluded",
              "Three days after the end",
            ],
            [
              "YouTube, integration",
              "Organic views, scaled by the lowest audience retention inside the branded segment",
              "Three days after the end",
            ],
          ]}
        />
        <Points
          items={[
            {
              text: "A creator links an account through the platform's own sign-in. Morcast's access is read-only.",
            },
            {
              text: "Reach that cannot be read from the platform is never estimated. The item fails.",
            },
            { text: "Morcast stores the platform's raw answers as evidence." },
          ]}
        />
      </DocSection>

      <DocSection id="checks" title="The checks">
        <P>
          Every item ends as a pass or a fail, with its reason and its evidence. The checks run in a
          fixed order, from objective rules to judgment.
        </P>
        <GuideSteps steps={checks} />
      </DocSection>

      <DocSection id="judgment" title="Judgment that can be checked">
        <Points
          items={[
            {
              lead: "Three runs, one verdict.",
              text: "Each model check runs three times. Three matching answers decide, and any disagreement goes to a person.",
            },
            {
              lead: "Pinned open models.",
              text: "The models are open-weight and pinned by exact version in each campaign's published terms, with the hash of every prompt. Anyone can run the same evaluation again. Morcast plans to run them on Morpheus inference once it returns the same verdicts reliably.",
            },
            {
              lead: "Judged as submitted.",
              text: "Compliance is judged on the content captured at submission. A later material edit fails the item.",
            },
          ]}
        />
      </DocSection>
    </DocPage>
  );
}
